import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const FilterControls = ({ 
  filters, 
  onFiltersChange, 
  onReset,
  className = '' 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const categoryOptions = [
    { value: '', label: 'All Categories' },
    { value: 'work', label: 'Work Contacts' },
    { value: 'personal', label: 'Personal Friends' },
    { value: 'family', label: 'Family Members' },
    { value: 'client', label: 'Clients' },
    { value: 'networking', label: 'Professional Network' }
  ];

  const lastContactOptions = [
    { value: '', label: 'Any Time' },
    { value: '7', label: 'Over 1 week ago' },
    { value: '30', label: 'Over 1 month ago' },
    { value: '90', label: 'Over 3 months ago' },
    { value: '180', label: 'Over 6 months ago' },
    { value: '365', label: 'Over 1 year ago' }
  ];

  const priorityOptions = [
    { value: '', label: 'All Priorities' },
    { value: 'high', label: 'High Priority' },
    { value: 'medium', label: 'Medium Priority' },
    { value: 'low', label: 'Low Priority' }
  ];

  const handleFilterChange = (key, value) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const hasActiveFilters = Object.values(filters)?.some(value => 
    value !== '' && value !== false && (Array.isArray(value) ? value?.length > 0 : true)
  );

  return (
    <div className={`bg-surface border border-border rounded-xl shadow-card ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-secondary rounded-lg flex items-center justify-center">
            <Icon name="Filter" size={16} color="white" />
          </div>
          <div>
            <h3 className="font-medium text-text-primary">Filter Options</h3>
            {hasActiveFilters && (
              <p className="text-xs text-primary">Filters active</p>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onReset}
              iconName="RotateCcw"
              iconPosition="left"
            >
              Reset
            </Button>
          )}
          
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={20} />
          </Button>
        </div>
      </div>
      {/* Filter Controls */}
      {isExpanded && (
        <div className="p-4 space-y-4 animate-slide-up">
          {/* Basic Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select
              label="Contact Category"
              options={categoryOptions}
              value={filters?.category || ''}
              onChange={(value) => handleFilterChange('category', value)}
            />

            <Select
              label="Last Contact"
              options={lastContactOptions}
              value={filters?.lastContactDays || ''}
              onChange={(value) => handleFilterChange('lastContactDays', value)}
            />

            <Select
              label="Priority Level"
              options={priorityOptions}
              value={filters?.priority || ''}
              onChange={(value) => handleFilterChange('priority', value)}
            />
          </div>

          {/* Advanced Options */}
          <div className="pt-4 border-t border-border">
            <h4 className="font-medium text-text-primary mb-3">Advanced Options</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <Checkbox
                  label="Include contacts with birthdays this month"
                  checked={filters?.includeBirthdays || false}
                  onChange={(e) => handleFilterChange('includeBirthdays', e?.target?.checked)}
                />
                
                <Checkbox
                  label="Exclude recently contacted (last 7 days)"
                  checked={filters?.excludeRecent || false}
                  onChange={(e) => handleFilterChange('excludeRecent', e?.target?.checked)}
                />
                
                <Checkbox
                  label="Only show contacts with notes"
                  checked={filters?.hasNotes || false}
                  onChange={(e) => handleFilterChange('hasNotes', e?.target?.checked)}
                />
              </div>

              <div className="space-y-3">
                <Checkbox
                  label="Prioritize work contacts"
                  checked={filters?.prioritizeWork || false}
                  onChange={(e) => handleFilterChange('prioritizeWork', e?.target?.checked)}
                />
                
                <Checkbox
                  label="Include contacts without phone numbers"
                  checked={filters?.includeNoPhone || false}
                  onChange={(e) => handleFilterChange('includeNoPhone', e?.target?.checked)}
                />
                
                <Checkbox
                  label="Show VIP contacts only"
                  checked={filters?.vipOnly || false}
                  onChange={(e) => handleFilterChange('vipOnly', e?.target?.checked)}
                />
              </div>
            </div>
          </div>

          {/* Algorithm Settings */}
          <div className="pt-4 border-t border-border">
            <h4 className="font-medium text-text-primary mb-3">Selection Algorithm</h4>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Randomness Level
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={filters?.randomness || 50}
                  onChange={(e) => handleFilterChange('randomness', parseInt(e?.target?.value))}
                  className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-text-secondary mt-1">
                  <span>Predictable</span>
                  <span>{filters?.randomness || 50}%</span>
                  <span>Random</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Priority Weight
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={filters?.priorityWeight || 30}
                  onChange={(e) => handleFilterChange('priorityWeight', parseInt(e?.target?.value))}
                  className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-text-secondary mt-1">
                  <span>Equal</span>
                  <span>{filters?.priorityWeight || 30}%</span>
                  <span>Priority First</span>
                </div>
              </div>
            </div>
          </div>

          {/* Apply Button */}
          <div className="pt-4 border-t border-border">
            <Button
              variant="default"
              onClick={() => setIsExpanded(false)}
              iconName="Check"
              iconPosition="left"
              className="w-full"
            >
              Apply Filters
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterControls;